import { parseSearchMask, findBySearchMask } from '../../core/imcmd/SearchMask.js';
import { DEFAULT_NS, executeOnItems } from '../../core/imcmd/types.js';
import { ViewCmdType } from '../IViewWidget.js';
import { ViewMode } from '../ViewOptions.js';
import { ViewWidgetHelper } from '../ViewWidgetHelper.js';
import { ViewUiCommand } from './ViewUiCommand.js';

// src/core/imcmd/ViewUiCommandProvider.ts
/**
 * Провайдер UI-команд для ViewWidget (namespace = "view").
 */
class ViewUiCommandProvider {
    constructor() {
        /** Идентификатор провайдера */
        this.id = "ViewWidget";
        /** Пространство имён провайдера */
        this.namespace = "view";
        /** Приоритет среди провайдеров одного NS */
        this.priority = 10;
        /** Разрешён вызов без префикса (DEFAULT_NS) */
        this.defaultNSAllowed = true;
    }
    /**
     * Исполняет команды управления ViewWidget.
     * Алгоритм: проверка NS/команды → цели (targetItems | маска в rawArgs[0] | fallback) → executeOnItems.
     * @param token Токен UI-команды
     * @param ctx Контекст выполнения
     */
    execute(token, ctx) {
        const nsOk = token.namespace === this.namespace
            || (this.defaultNSAllowed && token.namespace === DEFAULT_NS);
        if (!nsOk)
            return false;
        const cmd = token.command || "";
        if (!isKnownViewCommand(cmd))
            return false;
        // Цели: адресная форма или маска-как-первый-аргумент
        if (!token.targetItems || token.targetItems.length === 0) {
            if (token.rawArgs && token.rawArgs.length > 0 && token.rawArgs[0]) {
                const sm = parseSearchMask(token.rawArgs[0]);
                token.targetItems = findBySearchMask(ctx.viewHost, sm, ctx.sourceEl, ctx.uiNotMask) || [];
            }
            else {
                // Изменено поведение 13-11-2025 - fallback сделан только если небыло маски
                /*  const near = (ctx.sourceEl as HTMLElement | null) ?? null;
                  console.info('near', near);
                  if (near)
                      token.targetItems = [near];*/
                token.targetItems = [ctx.viewHost];
            }
            if (!token.targetItems || token.targetItems.length === 0)
                return false;
        }
        //console.log('token', token);
        // Здесь при необходимости можно ограничить мультирежим для отдельных команд (выбрасывать ошибку).
        return executeOnItems(token, ctx, (el) => {
            const view = ViewWidgetHelper.closestOnByName(el);
            if (!view)
                return false;
            switch (cmd) {
                /** Закрыть view */
                // view::closeView|<mask>|<viewOnly>|<dontAsc=false>
                case ViewUiCommand.closeView: {
                    const [_, rawViewOnly, rawDontAsc] = token.rawArgs ?? [];
                    // 2026-10-06: без явного viewOnly ("imcmd": "closeview" в конфиге или разметке) форма,
                    // единственная в боковой панели или диалоге, закрывается вместе с рамкой: иначе оставалась пустая
                    // панель с заголовком. Серверная команда close передает viewOnly явно.
                    const viewOnly = rawViewOnly === undefined || rawViewOnly === ""
                        ? !isSoleViewOfFrame(view.element)
                        : rawViewOnly !== "false";
                    const dontAsc = rawDontAsc === "true"; // default = false
                    view.close(viewOnly, dontAsc);
                    return true;
                }
                /** Перевод обработки команды на сервер */
                case ViewUiCommand.ctrl:
                    // вызываем легаси-метод
                    ViewWidgetHelper.closestOnByName(el)?.onCtrlClick(el);
                    return true;
                /** Полное обновление view (refresh) */
                case ViewUiCommand.refresh:
                /** Полное обновление view (reload) */
                case ViewUiCommand.reload:
                    // TODO: Стандартизироваться для использования маски
                    const reloadType = cmd == ViewUiCommand.refresh ? ViewCmdType.refresh : ViewCmdType.reload;
                    token.targetItems?.forEach((elem) => {
                        const view = ViewWidgetHelper.closestOnByName(elem);
                        if (!view?.element) {
                            console.error('[ViewUiCommand.reload] view не найдено');
                            return;
                        }
                        if (view.element == elem || !elem.dataset.key) {
                            view.reload(undefined, undefined, undefined, reloadType);
                        }
                        else {
                            view.reload(elem.dataset.key, undefined, undefined, reloadType);
                        }
                    });
                    return true;
                case ViewUiCommand.setRequiredValues:
                    {
                        const [_, valueStr] = token.rawArgs ?? [];
                        ViewWidgetHelper.closestOnByName(el)?.setRequiredValues(valueStr);
                        return true;
                    }
                case ViewUiCommand.reloadItem:
                    {
                        const itemKey = el.dataset.key;
                        if (!itemKey) {
                            console.error('[ViewUiCommand.reloadItem] У целевого элемента отсутствует data-key, перезагрузка невозможна');
                            return false;
                        }
                        // 2026-04-11
                        // В режиме reloadItem, если команда вызвана на корневом элементе текущего view,
                        // перечитываем родительский view. Это обновляет весь вложенный блок вместе
                        // со всеми параметрами, а не только внутреннее содержимое текущего view.
                        // Пример: C:\work\Nordbox.Web.Core\ContentConfigs\ods\Web\Areas\perm\Views\managerView\managerView.txt
                        if (view.element == el) {
                            const viewParent = ViewWidgetHelper.closestOnByName(el.parentElement);
                            if (viewParent == undefined) {
                                view.reload();
                            }
                            else {
                                viewParent?.reload(itemKey);
                            }
                            return true;
                        }
                        view.reload(itemKey);
                        return true;
                    }
                case ViewUiCommand.reloadResult:
                    {
                        if (!token.solvedArgs) {
                            console.error('Не получены значения reloadResult');
                            return false;
                        }
                        const html = token.solvedArgs.html;
                        const itemMode = token.solvedArgs.itemMode;
                        if (itemMode) {
                            // Замена элемента содержимым html
                            // Создаём временный контейнер, чтобы распарсить HTML
                            const temp = document.createElement('div');
                            temp.innerHTML = html.trim(); // Тут ожидаем что будет ресолен el
                            const newElement = temp.firstElementChild;
                            if (!newElement) {
                                console.error('HTML пустой, замена не выполнена');
                                return false;
                            }
                            const parenView = view.element.parentElement ? ViewWidgetHelper.closestOnByName(view.element.parentElement) : undefined;
                            //console.log('reloadResult', 'newElement', newElement);
                            // Заменяем элемент
                            if (el.dataset.widget == 'Radiobox' && el.closest('.im-radio-container')) {
                                //console.log('reloadResult Radiobox');
                                const elContainer = el.closest('.im-radio-container');
                                elContainer?.replaceWith(newElement);
                            }
                            else
                                el.replaceWith(newElement);
                            if (parenView)
                                parenView.dom.setXClass();
                            view.dom.setXClass();
                            // 2026-04-09: initHtml уже вызывает ImSys.initDom, второй проход здесь создавал повторную инициализацию.
                            view.initHtml(newElement);
                        }
                        else {
                            // Перезагрузка содержимого view
                            const reloadCont = view.dom.querySelector('.im-view-content') ?? view.element;
                            reloadCont.innerHTML = html;
                            const parenView = view.element.parentElement ? ViewWidgetHelper.closestOnByName(view.element.parentElement) : undefined;
                            if (parenView)
                                parenView.dom.setXClass();
                            view.dom.setXClass();
                            // 2026-04-09: initHtml уже вызывает ImSys.initDom, второй проход здесь создавал повторную инициализацию.
                            view.initHtml(reloadCont);
                        }
                        return true;
                    }
                case ViewUiCommand.copy: {
                    // Аргументы команды: ожидаем минимум 4 значения
                    // vals[0] → ключ view (куда копируем)
                    // vals[2] → ключ источника (откуда берём значение)
                    // vals[3] → ключ приёмника (куда пишем значение)
                    const vals = token.rawArgs ?? [];
                    if (vals.length >= 4) {
                        const [viewKey, , srcKey, dstKey] = vals;
                        // Находим целевой view по data-key
                        const viewEl = ctx.viewHost?.querySelector(`.im-view[data-key="${viewKey}"]`);
                        if (!viewEl)
                            return false;
                        // Источник: [data-key=srcKey] + uiNotMask (ограничение зоны поиска)
                        const srcEl = ctx.viewHost?.querySelector(`[data-key="${srcKey}"]${ctx.uiNotMask ?? ''}`);
                        // Приёмник: [data-key=dstKey] внутри найденного view
                        const dstEl = viewEl.querySelector(`[data-key="${dstKey}"]`);
                        // Если оба поля найдены и у них есть value → копируем
                        if (srcEl && dstEl && 'value' in srcEl && 'value' in dstEl) {
                            dstEl.value = srcEl.value;
                            return true;
                        }
                    }
                    console.error(`Ошибка аргументов копирования`);
                    return true;
                }
                // TODO перенести в default
                case ViewUiCommand.preventDefault: {
                    // Результат с сигналом preventDefault
                    return { preventDefault: true };
                }
                case ViewUiCommand.usecmd: {
                    // Результат с сигналом usecmd - выполнить серверную команду
                    return { usecmd: true };
                }
                case ViewUiCommand.saved: {
                    view.onSave();
                    return true;
                }
                case ViewUiCommand.setFormMode: {
                    const [_, rawMode] = token.rawArgs ?? [];
                    const mode = rawMode;
                    if (Object.values(ViewMode).includes(ViewMode[mode])) {
                        view.setMode(ViewMode[mode]);
                    }
                    else {
                        console.error(`setFormMode Тип ${rawMode} не поддерживается`);
                    }
                    return true;
                }
                case ViewUiCommand.showHelp: {
                    const source = ctx.sourceEl;
                    const values = view.encodeValues();
                    const isMainHelp = source.classList.contains("main-help") || source.closest(".main-help") !== null;
                    const viewKeysCsv = isMainHelp
                        ? collectGlobalHelpKeys(document)
                        : collectContextHelpKeys(view, document);
                    const options = {
                        views: viewKeysCsv
                    };
                    const request = { type: "gethelp", cmd: "", values, options: options, dataKey: view.options.dataKey, itemKey: source.getAttribute('data-key') ?? undefined };
                    // Выполняем запрос на отображение помощи
                    view.doJson(request, undefined, undefined, false);
                }
                default:
                    return false;
            }
        });
    }
}
/**
 * Проверяет, что view — единственное содержимое своей ближайшей рамки (боковой панели или диалога):
 * других view в рамке нет, кроме вложенных в него самого.
 * 2026-10-14: диалог im-dialog учитывается наравне с панелью, иначе closeview в диалоге
 * удалял только содержимое и оставлял пустую рамку с заголовком.
 * @param viewEl Элемент view
 */
function isSoleViewOfFrame(viewEl) {
    const frame = viewEl.closest("im-dialog, .im-modal-aside");
    if (!frame)
        return false;
    return !Array.from(frame.querySelectorAll(".im-view"))
        .some(other => other !== viewEl && !viewEl.contains(other));
}
/** Проверяет, что команда определена в enum ViewUiCommand. */
function isKnownViewCommand(cmd) {
    // string-enum → уже строка; numeric-enum → нужна обратная проекция
    return typeof cmd === 'string' ? cmd : ViewUiCommand[cmd];
}
const HELP_PRIORITY_RULES = [
    { selector: ".main-wrapper", priority: 100 },
    { selector: ".article-aside-detailed", priority: 80 },
    { selector: ".im-layout, .content-wrapper, .content, main", priority: 60 },
    { selector: ".aside-menu", priority: 40 },
    { selector: "header, .header", priority: 20 },
];
const DEFAULT_HELP_PRIORITY = 60;
/**
 * Глобальный help собирает весь контекст страницы без локальной приоритезации.
 */
function collectGlobalHelpKeys(root) {
    const keys = [];
    const seenKeys = new Set();
    const allViews = root.querySelectorAll(".im-view");
    allViews.forEach((el) => {
        const view = ViewWidgetHelper.closestOnByName(el);
        appendViewContextKeys(keys, seenKeys, view);
    });
    const helpInputs = root.querySelectorAll("input[type='hidden'][data-help]");
    helpInputs.forEach((input) => appendHelpKey(keys, seenKeys, input.dataset.help));
    return keys.join(",");
}
/**
 * Локальный help сначала собирает цепочку текущего view, затем остальной контекст страницы,
 * отсортированный по зоне интерфейса.
 */
function collectContextHelpKeys(currentView, root) {
    const keys = [];
    const seenKeys = new Set();
    const currentChain = getViewChain(currentView);
    currentChain.forEach((view) => appendViewContextKeys(keys, seenKeys, view));
    const remainingViews = getUniqueViews(root)
        .filter((view) => !currentChain.some((chainView) => chainView?.element === view?.element))
        .sort(compareViewsByHelpPriority);
    remainingViews.forEach((view) => appendViewContextKeys(keys, seenKeys, view));
    const helpInputs = root.querySelectorAll("input[type='hidden'][data-help]");
    helpInputs.forEach((input) => appendHelpKey(keys, seenKeys, input.dataset.help));
    return keys.join(",");
}
/**
 * Добавляет help-контекст view: сначала его viewKey, затем все скрытые data-help маркеры внутри.
 */
function appendViewContextKeys(keys, seenKeys, view) {
    if (!view?.element)
        return;
    appendHelpKey(keys, seenKeys, view.options.viewKey);
    const helpInputs = view.element.querySelectorAll("input[type='hidden'][data-help]");
    helpInputs.forEach((input) => appendHelpKey(keys, seenKeys, input.dataset.help));
}
function appendHelpKey(keys, seenKeys, key) {
    if (!key || seenKeys.has(key))
        return;
    seenKeys.add(key);
    keys.push(key);
}
/**
 * Возвращает уникальные view страницы по их корневому DOM-элементу.
 */
function getUniqueViews(root) {
    const result = [];
    const seenElements = new Set();
    root.querySelectorAll(".im-view").forEach((el) => {
        const view = ViewWidgetHelper.closestOnByName(el);
        if (!view?.element || seenElements.has(view.element))
            return;
        seenElements.add(view.element);
        result.push(view);
    });
    return result;
}
/**
 * Строит цепочку родительских view от текущего view к корню.
 */
function getViewChain(startView) {
    const chain = [];
    let currentView = startView;
    while (currentView?.element) {
        chain.push(currentView);
        currentView = getParentView(currentView);
    }
    return chain;
}
/**
 * Ищет ближайшее родительское view выше текущего DOM-хоста.
 */
function getParentView(view) {
    const parentElement = view.element?.parentElement;
    if (!parentElement)
        return undefined;
    return ViewWidgetHelper.closestOnByName(parentElement);
}
/**
 * Сортирует view по приоритету зоны интерфейса.
 * При одинаковом приоритете выше ставится view, расположенное ближе к корню.
 */
function compareViewsByHelpPriority(left, right) {
    const leftPriority = getHelpPriority(left?.element);
    const rightPriority = getHelpPriority(right?.element);
    if (leftPriority !== rightPriority)
        return rightPriority - leftPriority;
    const leftDepth = getViewDepth(left);
    const rightDepth = getViewDepth(right);
    return leftDepth - rightDepth;
}
/**
 * Определяет приоритет help по ближайшему значимому контейнеру интерфейса.
 */
function getHelpPriority(element) {
    if (!element)
        return DEFAULT_HELP_PRIORITY;
    for (const rule of HELP_PRIORITY_RULES) {
        if (element.closest(rule.selector))
            return rule.priority;
    }
    return DEFAULT_HELP_PRIORITY;
}
/**
 * Глубина view в иерархии parent view.
 */
function getViewDepth(view) {
    let depth = 0;
    let current = view;
    while (current?.element) {
        depth++;
        current = getParentView(current);
    }
    return depth;
}

export { DEFAULT_HELP_PRIORITY, HELP_PRIORITY_RULES, ViewUiCommandProvider, appendHelpKey, appendViewContextKeys, collectContextHelpKeys, collectGlobalHelpKeys, compareViewsByHelpPriority, getHelpPriority, getParentView, getUniqueViews, getViewChain, getViewDepth };
